import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddLocationAltOutlinedIcon from "@mui/icons-material/AddLocationAltOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import axios from "axios";
import { buildApiUrl } from "./apiConfig";

const EMPTY_ADDRESS = {
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "Romania",
  categorie: "",
};

const EMPTY_CONFIDENTIAL_INFO = {
  cnp: "",
  serieCI: "",
  nrCI: "",
};

const CONFIDENTIAL_FIELDS = [
  { key: "cnp", label: "CNP" },
  { key: "serieCI", label: "Serie CI" },
  { key: "nrCI", label: "Număr CI" },
];

const EMPTY_VENDOR_DRAFT = {
  name: "",
  email: "",
  phone: "",
  bank_name: "",
  iban: "",
};

const ADDRESS_CATEGORIES = [
  { value: "ci", label: "Adresă CI" },
  { value: "ridicat", label: "Adresă de ridicare" },
  { value: "ridicat2", label: "Adresă secundară de ridicare" },
  { value: "retur", label: "Adresă de retur" },
];

const CATEGORY_LABELS = ADDRESS_CATEGORIES.reduce(
  (labels, category) => ({ ...labels, [category.value]: category.label }),
  {},
);

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    backgroundColor: "#fff",
  },
};

const getApiErrorMessage = (error, fallback) => {
  const apiMessage = error?.response?.data?.message;
  return typeof apiMessage === "string" && apiMessage.trim()
    ? apiMessage
    : fallback;
};

const normalizeEmail = (value) => String(value || "").trim();

const cleanVendorDraft = (vendor) => ({
  name: String(vendor.name || "").trim(),
  email: normalizeEmail(vendor.email).toLowerCase(),
  phone: String(vendor.phone || "").trim(),
  bank_name: String(vendor.bank_name || "").trim(),
  iban: String(vendor.iban || "")
    .replace(/\s+/g, "")
    .toUpperCase(),
});

const validateVendorDraft = (vendor) => {
  const errors = {};
  if (!vendor.name) errors.name = "Numele furnizorului este obligatoriu.";
  if (!vendor.email) {
    errors.email = "Emailul furnizorului este obligatoriu.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vendor.email)) {
    errors.email = "Emailul nu are un format valid.";
  }
  if (!vendor.phone) errors.phone = "Telefonul este obligatoriu.";
  if (!vendor.iban) errors.iban = "IBAN-ul este obligatoriu.";
  return errors;
};

const cleanAddress = (address) => ({
  address_line1: String(address.address_line1 || "").trim(),
  address_line2: String(address.address_line2 || "").trim(),
  city: String(address.city || "").trim(),
  state: String(address.state || "").trim(),
  postal_code: String(address.postal_code || "").trim(),
  country: String(address.country || "Romania").trim(),
  categorie: String(address.categorie || "").trim(),
});

const cleanConfidentialInfo = (info) => ({
  cnp: String(info.cnp || "").trim(),
  serieCI: String(info.serieCI || "")
    .trim()
    .toUpperCase(),
  nrCI: String(info.nrCI || "").trim(),
});

const validateAddress = (address) => {
  const errors = {};
  if (!address.address_line1.trim()) {
    errors.address_line1 = "Adresa este obligatorie.";
  }
  if (!address.city.trim()) {
    errors.city = "Localitatea este obligatorie.";
  }
  if (!address.country.trim()) {
    errors.country = "Țara este obligatorie.";
  }
  if (!address.categorie) {
    errors.categorie = "Selectează tipul adresei.";
  }
  if (address.categorie === "ci" && !address.state.trim()) {
    errors.state = "Județul este obligatoriu pentru adresa din CI.";
  }
  if (address.categorie === "ci" && !address.postal_code.trim()) {
    errors.postal_code = "Codul poștal este obligatoriu pentru adresa din CI.";
  }
  return errors;
};

const validateConfidentialInfo = (info) => {
  const errors = {};
  if (![info.cnp, info.serieCI, info.nrCI].some((value) => value.trim())) {
    errors.form =
      "Completează cel puțin unul dintre câmpurile CNP, serie CI sau număr CI.";
  }
  return errors;
};

function SectionTitle({ icon, title, description, action }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      spacing={2}
      sx={{ mb: 2 }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 42,
            height: 42,
            display: "grid",
            placeItems: "center",
            color: "#4338ca",
            backgroundColor: "#eef2ff",
            borderRadius: 2.5,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 750, color: "#172033" }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "#667085", mt: 0.25 }}>
            {description}
          </Typography>
        </Box>
      </Stack>
      {action}
    </Stack>
  );
}

function AddressForm({
  value,
  onChange,
  errors,
  onSave,
  onCancel,
  saving,
  submitLabel,
}) {
  const updateField = (field, fieldValue) => {
    onChange((current) => ({ ...current, [field]: fieldValue }));
  };

  return (
    <Box
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
      noValidate
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        <TextField
          label="Adresă"
          placeholder="Stradă, număr, bloc, scară"
          value={value.address_line1}
          onChange={(event) => updateField("address_line1", event.target.value)}
          error={Boolean(errors.address_line1)}
          helperText={errors.address_line1}
          required
          autoFocus
          fullWidth
          sx={{ ...inputSx, gridColumn: { md: "1 / -1" } }}
        />
        <TextField
          label="Detalii adresă"
          placeholder="Apartament, etaj, reper (opțional)"
          value={value.address_line2}
          onChange={(event) => updateField("address_line2", event.target.value)}
          fullWidth
          sx={{ ...inputSx, gridColumn: { md: "1 / -1" } }}
        />
        <TextField
          label="Localitate"
          value={value.city}
          onChange={(event) => updateField("city", event.target.value)}
          error={Boolean(errors.city)}
          helperText={errors.city}
          required
          fullWidth
          sx={inputSx}
        />
        <TextField
          label="Județ / regiune"
          value={value.state}
          onChange={(event) => updateField("state", event.target.value)}
          error={Boolean(errors.state)}
          helperText={errors.state}
          required={value.categorie === "ci"}
          fullWidth
          sx={inputSx}
        />
        <TextField
          label="Cod poștal"
          value={value.postal_code}
          onChange={(event) => updateField("postal_code", event.target.value)}
          error={Boolean(errors.postal_code)}
          helperText={errors.postal_code}
          required={value.categorie === "ci"}
          fullWidth
          sx={inputSx}
        />
        <TextField
          select
          label="Țară"
          value={value.country}
          onChange={(event) => updateField("country", event.target.value)}
          error={Boolean(errors.country)}
          helperText={errors.country}
          required
          fullWidth
          sx={inputSx}
          SelectProps={{ native: true }}
        >
          <option value="Romania">România</option>
          <option value="United States">Statele Unite</option>
          <option value="Canada">Canada</option>
        </TextField>
        <TextField
          select
          label="Tip adresă"
          value={value.categorie}
          onChange={(event) => updateField("categorie", event.target.value)}
          error={Boolean(errors.categorie)}
          helperText={errors.categorie}
          required
          fullWidth
          sx={{ ...inputSx, gridColumn: { md: "1 / -1" } }}
          SelectProps={{ native: true }}
        >
          <option value="">Selectează tipul adresei</option>
          {ADDRESS_CATEGORIES.map((category) => (
            <option key={category.value} value={category.value}>
              {category.label}
            </option>
          ))}
        </TextField>
      </Box>

      <Stack
        direction={{ xs: "column-reverse", sm: "row" }}
        justifyContent="flex-end"
        spacing={1.25}
        sx={{ mt: 2.5 }}
      >
        <Button
          type="button"
          variant="text"
          startIcon={<CancelOutlinedIcon />}
          onClick={onCancel}
          disabled={saving}
          sx={{ color: "#475467" }}
        >
          Renunță
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={
            saving ? (
              <CircularProgress size={17} color="inherit" />
            ) : (
              <SaveOutlinedIcon />
            )
          }
          disabled={saving}
          sx={{
            borderRadius: 2,
            px: 2.5,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
          }}
        >
          {saving ? "Se salvează..." : submitLabel}
        </Button>
      </Stack>
    </Box>
  );
}

export default function VendorInfo({
  email,
  initialName = "",
  initialPhone = "",
  initialBankName = "",
  initialIban = "",
  onAddressSelect,
  onVendorDataChange,
}) {
  const onVendorDataChangeRef = useRef(onVendorDataChange);
  const onAddressSelectRef = useRef(onAddressSelect);
  const emailRef = useRef(email);
  const fetchSequenceRef = useRef(0);
  const createSequenceRef = useRef(0);
  const createRequestRef = useRef(null);
  const vendorDefaultsRef = useRef({ ...EMPTY_VENDOR_DRAFT });
  const vendorDataRef = useRef(null);
  emailRef.current = email;
  vendorDefaultsRef.current = {
    name: initialName,
    email,
    phone: initialPhone,
    bank_name: initialBankName,
    iban: initialIban,
  };
  const [vendorData, setVendorData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [vendorMissing, setVendorMissing] = useState(false);
  const [vendorDraft, setVendorDraft] = useState({ ...EMPTY_VENDOR_DRAFT });
  const [vendorDraftErrors, setVendorDraftErrors] = useState({});
  const [createVendorError, setCreateVendorError] = useState("");
  const [creatingVendor, setCreatingVendor] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState("");

  const [addingAddress, setAddingAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({ ...EMPTY_ADDRESS });
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressErrors, setAddressErrors] = useState({});
  const [savingAddress, setSavingAddress] = useState(false);
  const [deletingAddress, setDeletingAddress] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);

  const [confidentialPanelOpen, setConfidentialPanelOpen] = useState(false);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  const [confidentialInfo, setConfidentialInfo] = useState({
    ...EMPTY_CONFIDENTIAL_INFO,
  });
  const [savedConfidentialInfo, setSavedConfidentialInfo] = useState({
    ...EMPTY_CONFIDENTIAL_INFO,
  });
  const [confidentialStatus, setConfidentialStatus] = useState({
    hasConfidentialInfo: false,
    isComplete: false,
  });
  const [confidentialErrors, setConfidentialErrors] = useState({});
  const [savingConfidentialInfo, setSavingConfidentialInfo] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  useEffect(() => {
    onVendorDataChangeRef.current = onVendorDataChange;
    onAddressSelectRef.current = onAddressSelect;
  }, [onAddressSelect, onVendorDataChange]);

  const publishVendorData = useCallback((nextVendorData) => {
    vendorDataRef.current = nextVendorData;
    setVendorData(nextVendorData);
    onVendorDataChangeRef.current?.(nextVendorData);
    return nextVendorData;
  }, []);

  const showNotification = useCallback((message, severity = "success") => {
    setNotification({ open: true, message, severity });
  }, []);

  const isCurrentVendorRequest = useCallback((requestEmail, vendorId) => {
    return (
      normalizeEmail(emailRef.current) === requestEmail &&
      String(vendorDataRef.current?.vendor?.vendor_id) === String(vendorId)
    );
  }, []);

  const fetchVendorData = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = ++fetchSequenceRef.current;
      const normalizedEmail = normalizeEmail(emailRef.current);
      if (!normalizedEmail) {
        publishVendorData(null);
        setFetchError("");
        setVendorMissing(false);
        setLoading(false);
        return null;
      }

      if (!silent) setLoading(true);
      setFetchError("");

      try {
        const response = await axios.get(
          buildApiUrl(
            `/api/tasks/vendor/${encodeURIComponent(normalizedEmail)}`,
          ),
        );
        const normalizedData = {
          ...response.data,
          addresses: Array.isArray(response.data?.addresses)
            ? response.data.addresses
            : [],
          contactHistory: Array.isArray(response.data?.contactHistory)
            ? response.data.contactHistory
            : [],
        };
        if (
          requestId !== fetchSequenceRef.current ||
          normalizeEmail(emailRef.current) !== normalizedEmail
        ) {
          return null;
        }
        setVendorMissing(false);
        setCreateVendorError("");
        publishVendorData(normalizedData);
        return normalizedData;
      } catch (error) {
        if (
          requestId !== fetchSequenceRef.current ||
          normalizeEmail(emailRef.current) !== normalizedEmail
        ) {
          return null;
        }
        const isMissing = error?.response?.status === 404;
        const message = isMissing
          ? "Furnizorul nu este încă în baza de date. Îl poți inițializa direct folosind formularul de mai jos."
          : getApiErrorMessage(
              error,
              "Nu am putut încărca informațiile furnizorului. Încearcă din nou.",
            );
        setVendorMissing(isMissing);
        if (isMissing) {
          setVendorDraft(cleanVendorDraft(vendorDefaultsRef.current));
          setVendorDraftErrors({});
          setCreateVendorError("");
        }
        setFetchError(message);
        if (!silent) publishVendorData(null);
        return null;
      } finally {
        if (
          !silent &&
          requestId === fetchSequenceRef.current &&
          normalizeEmail(emailRef.current) === normalizedEmail
        ) {
          setLoading(false);
        }
      }
    },
    [publishVendorData],
  );

  useEffect(() => {
    createSequenceRef.current += 1;
    createRequestRef.current = null;
    publishVendorData(null);
    onAddressSelectRef.current?.(null);
    setSelectedAddress("");
    setVendorMissing(false);
    setVendorDraft({ ...EMPTY_VENDOR_DRAFT });
    setVendorDraftErrors({});
    setCreateVendorError("");
    setCreatingVendor(false);
    setAddingAddress(false);
    setEditingAddress(null);
    setAddressErrors({});
    setConfidentialPanelOpen(false);
    setPasswordVerified(false);
    setPasswordInput("");
    setPasswordError("");
    setConfidentialInfo({ ...EMPTY_CONFIDENTIAL_INFO });
    setSavedConfidentialInfo({ ...EMPTY_CONFIDENTIAL_INFO });
    setConfidentialStatus({
      hasConfidentialInfo: false,
      isComplete: false,
    });
    fetchVendorData();
  }, [email, fetchVendorData, publishVendorData]);

  useEffect(() => {
    if (!vendorMissing || creatingVendor) return;

    const defaults = cleanVendorDraft({
      name: initialName,
      email,
      phone: initialPhone,
      bank_name: initialBankName,
      iban: initialIban,
    });
    setVendorDraft((current) => ({
      name: current.name || defaults.name,
      email: defaults.email,
      phone: current.phone || defaults.phone,
      bank_name: current.bank_name || defaults.bank_name,
      iban: current.iban || defaults.iban,
    }));
  }, [
    creatingVendor,
    email,
    initialBankName,
    initialIban,
    initialName,
    initialPhone,
    vendorMissing,
  ]);

  const addresses = useMemo(
    () => (Array.isArray(vendorData?.addresses) ? vendorData.addresses : []),
    [vendorData],
  );

  const updateVendorData = useCallback((updater) => {
    const current = vendorDataRef.current;
    if (!current) return;
    const next = updater(current);
    if (next === current) return;
    vendorDataRef.current = next;
    setVendorData(next);
    onVendorDataChangeRef.current?.(next);
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.get(buildApiUrl("/api/tasks/jira-vendor-sync"));
      const refreshedVendor = await fetchVendorData();
      if (refreshedVendor) {
        showNotification("Informațiile furnizorului au fost sincronizate.");
      } else {
        showNotification(
          "Sincronizarea a pornit, dar furnizorul nu este încă disponibil. Reîncearcă în câteva secunde.",
          "warning",
        );
      }
    } catch (error) {
      showNotification(
        getApiErrorMessage(
          error,
          "Sincronizarea nu a putut fi finalizată. Încearcă din nou.",
        ),
        "error",
      );
    } finally {
      setSyncing(false);
    }
  };

  const updateVendorDraftField = (field, value) => {
    setVendorDraft((current) => ({ ...current, [field]: value }));
    setVendorDraftErrors((current) => ({ ...current, [field]: "" }));
    setCreateVendorError("");
  };

  const handleCreateVendor = async (event) => {
    event.preventDefault();
    if (createRequestRef.current) return;

    const requestEmail = normalizeEmail(emailRef.current).toLowerCase();
    const payload = cleanVendorDraft({
      ...vendorDraft,
      email: requestEmail,
    });
    const validationErrors = validateVendorDraft(payload);
    setVendorDraftErrors(validationErrors);
    setCreateVendorError("");
    if (Object.keys(validationErrors).length > 0) return;

    const requestId = ++createSequenceRef.current;
    const requestToken = { requestId, requestEmail };
    createRequestRef.current = requestToken;
    setCreatingVendor(true);

    const isCurrentRequest = () =>
      createRequestRef.current === requestToken &&
      createSequenceRef.current === requestId &&
      normalizeEmail(emailRef.current).toLowerCase() === requestEmail;

    try {
      const response = await axios.post(
        buildApiUrl("/api/tasks/vendor"),
        payload,
      );
      if (!isCurrentRequest()) return;

      const responseVendor = response.data?.vendor;
      let initializedVendor = responseVendor?.vendor_id
        ? {
            ...response.data,
            vendor: responseVendor,
            addresses: Array.isArray(response.data?.addresses)
              ? response.data.addresses
              : [],
            contactHistory: Array.isArray(response.data?.contactHistory)
              ? response.data.contactHistory
              : [],
          }
        : null;

      if (!initializedVendor) {
        initializedVendor = await fetchVendorData({ silent: true });
      }
      if (!isCurrentRequest()) return;
      if (!initializedVendor?.vendor?.vendor_id) {
        throw new Error("Răspunsul nu conține identificatorul furnizorului.");
      }

      publishVendorData(initializedVendor);
      setVendorMissing(false);
      setFetchError("");
      setVendorDraftErrors({});
      setCreateVendorError("");
      setEditingAddress(null);
      setNewAddress({ ...EMPTY_ADDRESS, categorie: "ci" });
      setAddressErrors({});
      setAddingAddress(true);
      showNotification(
        response.status === 201
          ? "Furnizorul a fost creat. Poți adăuga acum prima adresă."
          : "Furnizorul există deja și a fost încărcat. Poți adăuga adresa.",
      );
    } catch (error) {
      if (!isCurrentRequest()) return;
      setCreateVendorError(
        getApiErrorMessage(
          error,
          "Furnizorul nu a putut fi creat. Verifică datele și încearcă din nou.",
        ),
      );
    } finally {
      if (createRequestRef.current === requestToken) {
        createRequestRef.current = null;
        setCreatingVendor(false);
      }
    }
  };

  const handleAddressSelect = (addressId) => {
    const address = addresses.find(
      (candidate) => String(candidate.address_id) === String(addressId),
    );
    if (!address) return;
    setSelectedAddress(String(address.address_id));
    onAddressSelectRef.current?.(address);
  };

  const startAddingAddress = () => {
    setEditingAddress(null);
    setNewAddress({ ...EMPTY_ADDRESS });
    setAddressErrors({});
    setAddingAddress(true);
  };

  const startEditingAddress = (address) => {
    setAddingAddress(false);
    setAddressErrors({});
    setEditingAddress({ ...address, ...cleanAddress(address) });
  };

  const handleAddAddress = async () => {
    const vendorId = vendorData?.vendor?.vendor_id;
    const requestEmail = normalizeEmail(emailRef.current);
    if (!vendorId) {
      showNotification(
        "Furnizorul nu este încă disponibil. Reîncarcă informațiile și încearcă din nou.",
        "error",
      );
      return;
    }

    const payload = cleanAddress(newAddress);
    const validationErrors = validateAddress(payload);
    setAddressErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSavingAddress(true);
    try {
      const response = await axios.post(buildApiUrl("/api/tasks/addresses"), {
        vendor_id: vendorId,
        ...payload,
      });
      if (!isCurrentVendorRequest(requestEmail, vendorId)) return;
      const savedAddress = response.data?.address || {
        ...payload,
        address_id: response.data?.address_id,
        vendor_id: vendorId,
      };
      updateVendorData((current) => ({
        ...current,
        addresses: [...(current.addresses || []), savedAddress],
      }));
      setAddingAddress(false);
      setNewAddress({ ...EMPTY_ADDRESS });
      setAddressErrors({});
      showNotification("Adresa a fost adăugată cu succes.");
    } catch (error) {
      if (!isCurrentVendorRequest(requestEmail, vendorId)) return;
      showNotification(
        getApiErrorMessage(
          error,
          "Adresa nu a putut fi salvată. Verifică datele și încearcă din nou.",
        ),
        "error",
      );
    } finally {
      setSavingAddress(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingAddress?.address_id) return;
    const vendorId = vendorData?.vendor?.vendor_id;
    const requestEmail = normalizeEmail(emailRef.current);

    const payload = cleanAddress(editingAddress);
    const validationErrors = validateAddress(payload);
    setAddressErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSavingAddress(true);
    try {
      const response = await axios.put(
        buildApiUrl(`/api/tasks/addresses/${editingAddress.address_id}`),
        payload,
      );
      if (!isCurrentVendorRequest(requestEmail, vendorId)) return;
      const savedAddress = response.data?.address || {
        ...editingAddress,
        ...payload,
      };
      updateVendorData((current) => ({
        ...current,
        addresses: (current.addresses || []).map((address) =>
          String(address.address_id) === String(savedAddress.address_id)
            ? savedAddress
            : address,
        ),
      }));
      if (String(selectedAddress) === String(savedAddress.address_id)) {
        onAddressSelectRef.current?.(savedAddress);
      }
      setEditingAddress(null);
      setAddressErrors({});
      showNotification("Adresa a fost actualizată.");
    } catch (error) {
      if (!isCurrentVendorRequest(requestEmail, vendorId)) return;
      showNotification(
        getApiErrorMessage(
          error,
          "Adresa nu a putut fi actualizată. Încearcă din nou.",
        ),
        "error",
      );
    } finally {
      setSavingAddress(false);
    }
  };

  const handleDeleteAddress = async () => {
    if (!addressToDelete?.address_id) return;
    const vendorId = vendorData?.vendor?.vendor_id;
    const requestEmail = normalizeEmail(emailRef.current);
    setDeletingAddress(true);

    try {
      await axios.delete(
        buildApiUrl(`/api/tasks/addresses/${addressToDelete.address_id}`),
      );
      if (!isCurrentVendorRequest(requestEmail, vendorId)) return;
      updateVendorData((current) => ({
        ...current,
        addresses: (current.addresses || []).filter(
          (address) =>
            String(address.address_id) !== String(addressToDelete.address_id),
        ),
      }));
      if (String(selectedAddress) === String(addressToDelete.address_id)) {
        setSelectedAddress("");
        onAddressSelectRef.current?.(null);
      }
      if (
        String(editingAddress?.address_id) ===
        String(addressToDelete.address_id)
      ) {
        setEditingAddress(null);
      }
      setAddressToDelete(null);
      showNotification("Adresa a fost ștearsă.");
    } catch (error) {
      if (!isCurrentVendorRequest(requestEmail, vendorId)) return;
      showNotification(
        getApiErrorMessage(error, "Adresa nu a putut fi ștearsă."),
        "error",
      );
    } finally {
      setDeletingAddress(false);
    }
  };

  const openConfidentialPanel = () => {
    setConfidentialPanelOpen(true);
    setPasswordVerified(false);
    setPasswordInput("");
    setPasswordError("");
    setConfidentialErrors({});
    setConfidentialInfo({ ...EMPTY_CONFIDENTIAL_INFO });
    setSavedConfidentialInfo({ ...EMPTY_CONFIDENTIAL_INFO });
    setConfidentialStatus({
      hasConfidentialInfo: false,
      isComplete: false,
    });
  };

  const closeConfidentialPanel = () => {
    setConfidentialPanelOpen(false);
    setPasswordVerified(false);
    setPasswordInput("");
    setPasswordError("");
    setConfidentialErrors({});
    setConfidentialInfo({ ...EMPTY_CONFIDENTIAL_INFO });
    setSavedConfidentialInfo({ ...EMPTY_CONFIDENTIAL_INFO });
    setConfidentialStatus({
      hasConfidentialInfo: false,
      isComplete: false,
    });
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    const vendorId = vendorData?.vendor?.vendor_id;
    const requestEmail = normalizeEmail(emailRef.current);
    const password = passwordInput.trim();

    if (!vendorId) {
      setPasswordError(
        "Furnizorul nu este disponibil. Reîncarcă informațiile.",
      );
      return;
    }
    if (!password) {
      setPasswordError("Introdu parola pentru a continua.");
      return;
    }

    setVerifyingPassword(true);
    setPasswordError("");
    try {
      const response = await axios.get(
        buildApiUrl(`/api/tasks/vendor/${vendorId}/confidential-info`),
        {
          headers: {
            "X-Vendor-Confidential-Password": password,
          },
        },
      );
      if (!isCurrentVendorRequest(requestEmail, vendorId)) return;
      const loadedInfo = cleanConfidentialInfo(
        response.data?.confidentialInfo || EMPTY_CONFIDENTIAL_INFO,
      );
      setConfidentialInfo(loadedInfo);
      setSavedConfidentialInfo(loadedInfo);
      setConfidentialStatus({
        hasConfidentialInfo: Boolean(response.data?.hasConfidentialInfo),
        isComplete: Boolean(response.data?.isComplete),
      });
      setPasswordVerified(true);
      setPasswordInput("");

      updateVendorData((current) => ({
        ...current,
        vendor: { ...current.vendor, ...loadedInfo },
      }));
    } catch (error) {
      if (!isCurrentVendorRequest(requestEmail, vendorId)) return;
      const message = getApiErrorMessage(
        error,
        error?.response?.status === 401
          ? "Parolă incorectă. Încearcă din nou."
          : "Datele confidențiale nu au putut fi verificate. Încearcă din nou.",
      );
      setPasswordError(message);
    } finally {
      setVerifyingPassword(false);
    }
  };

  const handleSaveConfidentialInfo = async () => {
    const vendorId = vendorData?.vendor?.vendor_id;
    const requestEmail = normalizeEmail(emailRef.current);
    if (!vendorId) {
      showNotification("Furnizorul nu este disponibil.", "error");
      return;
    }

    const payload = cleanConfidentialInfo(confidentialInfo);
    const validationErrors = validateConfidentialInfo(payload);
    setConfidentialErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSavingConfidentialInfo(true);
    try {
      const response = await axios.post(
        buildApiUrl("/api/tasks/vendor/confidential-info"),
        {
          vendor_id: vendorId,
          ...payload,
        },
      );
      if (!isCurrentVendorRequest(requestEmail, vendorId)) return;
      const savedInfo = cleanConfidentialInfo(
        response.data?.confidentialInfo || payload,
      );
      const hasConfidentialInfo =
        typeof response.data?.hasConfidentialInfo === "boolean"
          ? response.data.hasConfidentialInfo
          : Object.values(savedInfo).some(Boolean);
      const isComplete =
        typeof response.data?.isComplete === "boolean"
          ? response.data.isComplete
          : Object.values(savedInfo).every(Boolean);

      setConfidentialInfo(savedInfo);
      setSavedConfidentialInfo(savedInfo);
      setConfidentialStatus({ hasConfidentialInfo, isComplete });
      updateVendorData((current) => ({
        ...current,
        vendor: { ...current.vendor, ...savedInfo },
      }));
      setConfidentialErrors({});
      showNotification("Datele confidențiale au fost salvate cu succes.");
    } catch (error) {
      if (!isCurrentVendorRequest(requestEmail, vendorId)) return;
      showNotification(
        getApiErrorMessage(
          error,
          "Datele confidențiale nu au putut fi salvate. Verifică toate câmpurile.",
        ),
        "error",
      );
    } finally {
      setSavingConfidentialInfo(false);
    }
  };

  const confidentialAlert = !confidentialStatus.hasConfidentialInfo
    ? {
        severity: "info",
        text: "Nu există informații confidențiale adăugate pentru acest furnizor. Completează câmpurile de mai jos pentru a le salva.",
      }
    : confidentialStatus.isComplete
      ? {
          severity: "success",
          text: "Am găsit informații confidențiale salvate. Le poți verifica sau actualiza mai jos.",
        }
      : {
          severity: "warning",
          text: "Există informații confidențiale incomplete. Completează toate câmpurile pentru a le actualiza.",
        };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 4,
        borderColor: "#e4e7ec",
        overflow: "hidden",
        boxShadow: "0 12px 30px rgba(16, 24, 40, 0.06)",
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2.25,
          background:
            "linear-gradient(135deg, rgba(238,242,255,0.95) 0%, rgba(255,255,255,1) 62%, rgba(236,253,245,0.8) 100%)",
          borderBottom: "1px solid #eaecf0",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 46,
                height: 46,
                display: "grid",
                placeItems: "center",
                borderRadius: 3,
                color: "#fff",
                background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                boxShadow: "0 8px 20px rgba(79, 70, 229, 0.25)",
              }}
            >
              <BadgeOutlinedIcon />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: "#101828", lineHeight: 1.25 }}
              >
                Informații furnizor
              </Typography>
              <Typography variant="body2" sx={{ color: "#667085", mt: 0.3 }}>
                {email || "Nu este selectat niciun furnizor"}
              </Typography>
            </Box>
          </Stack>

          <Tooltip title="Sincronizează informațiile">
            <span>
              <IconButton
                onClick={handleSync}
                disabled={!email || syncing || creatingVendor}
                aria-label="Sincronizează informațiile furnizorului"
                sx={{
                  border: "1px solid #d0d5dd",
                  backgroundColor: "rgba(255,255,255,0.85)",
                  "&:hover": { backgroundColor: "#fff" },
                }}
              >
                {syncing ? <CircularProgress size={21} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <CardContent sx={{ p: { xs: 2, sm: 3 }, "&:last-child": { pb: 3 } }}>
        {!email && (
          <Alert severity="info" sx={{ borderRadius: 2.5 }}>
            Selectează sau încarcă un furnizor pentru a vedea informațiile sale.
          </Alert>
        )}

        {email && loading && (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" sx={{ color: "#667085" }}>
              Se încarcă informațiile furnizorului...
            </Typography>
          </Stack>
        )}

        {email && !loading && fetchError && !vendorMissing && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => fetchVendorData()}
              >
                Reîncearcă
              </Button>
            }
            sx={{ borderRadius: 2.5 }}
          >
            {fetchError}
          </Alert>
        )}

        {email && !loading && vendorMissing && (
          <Paper
            component="section"
            variant="outlined"
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 3.5,
              borderColor: "#c7d2fe",
              background:
                "linear-gradient(145deg, #f8faff 0%, #ffffff 60%, #f0fdf4 100%)",
              boxShadow: "0 10px 26px rgba(79, 70, 229, 0.08)",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    flex: "0 0 auto",
                    display: "grid",
                    placeItems: "center",
                    borderRadius: 3,
                    color: "#4338ca",
                    backgroundColor: "#e0e7ff",
                  }}
                >
                  <PersonAddAltOutlinedIcon />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, color: "#1d2939" }}>
                    Inițializează furnizorul
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#667085", mt: 0.3 }}
                  >
                    Verifică datele preluate din cerere înainte de salvare.
                  </Typography>
                </Box>
              </Stack>
              <Chip
                size="small"
                label="Furnizor nou"
                sx={{
                  color: "#3730a3",
                  backgroundColor: "#eef2ff",
                  fontWeight: 700,
                }}
              />
            </Stack>

            <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2.5 }}>
              {fetchError} După creare, formularul pentru prima adresă se va
              deschide automat.
            </Alert>

            <Box component="form" onSubmit={handleCreateVendor} noValidate>
              {createVendorError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
                  {createVendorError}
                </Alert>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <TextField
                  label="Nume furnizor"
                  value={vendorDraft.name}
                  onChange={(event) =>
                    updateVendorDraftField("name", event.target.value)
                  }
                  error={Boolean(vendorDraftErrors.name)}
                  helperText={vendorDraftErrors.name}
                  required
                  fullWidth
                  autoComplete="name"
                  sx={inputSx}
                />
                <TextField
                  label="Email"
                  value={vendorDraft.email}
                  error={Boolean(vendorDraftErrors.email)}
                  helperText={
                    vendorDraftErrors.email ||
                    "Emailul este identificatorul furnizorului și se modifică din cerere."
                  }
                  required
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={inputSx}
                />
                <TextField
                  label="Telefon"
                  value={vendorDraft.phone}
                  onChange={(event) =>
                    updateVendorDraftField("phone", event.target.value)
                  }
                  error={Boolean(vendorDraftErrors.phone)}
                  helperText={vendorDraftErrors.phone}
                  required
                  fullWidth
                  autoComplete="tel"
                  sx={inputSx}
                />
                <TextField
                  label="Bancă (opțional)"
                  value={vendorDraft.bank_name}
                  onChange={(event) =>
                    updateVendorDraftField("bank_name", event.target.value)
                  }
                  fullWidth
                  autoComplete="off"
                  sx={inputSx}
                />
                <TextField
                  label="IBAN"
                  value={vendorDraft.iban}
                  onChange={(event) =>
                    updateVendorDraftField(
                      "iban",
                      event.target.value.toUpperCase(),
                    )
                  }
                  error={Boolean(vendorDraftErrors.iban)}
                  helperText={vendorDraftErrors.iban}
                  required
                  fullWidth
                  inputProps={{ maxLength: 42 }}
                  sx={{ ...inputSx, gridColumn: { md: "1 / -1" } }}
                />
              </Box>

              <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                justifyContent="flex-end"
                spacing={1.25}
                sx={{ mt: 2.5 }}
              >
                <Button
                  type="button"
                  variant="text"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchVendorData()}
                  disabled={creatingVendor}
                  sx={{ color: "#475467", textTransform: "none" }}
                >
                  Verifică din nou
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={creatingVendor}
                  startIcon={
                    creatingVendor ? (
                      <CircularProgress size={17} color="inherit" />
                    ) : (
                      <PersonAddAltOutlinedIcon />
                    )
                  }
                  sx={{
                    borderRadius: 2.5,
                    px: 2.5,
                    py: 1,
                    textTransform: "none",
                    fontWeight: 750,
                    boxShadow: "none",
                    background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                  }}
                >
                  {creatingVendor
                    ? "Se creează furnizorul..."
                    : "Creează și adaugă adresa"}
                </Button>
              </Stack>
            </Box>
          </Paper>
        )}

        {email && !loading && vendorData && (
          <Stack spacing={3}>
            <Box>
              <SectionTitle
                icon={<LocationOnOutlinedIcon />}
                title="Adrese"
                description="Selectează adresa folosită pentru această cerere."
                action={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={`${addresses.length} ${addresses.length === 1 ? "adresă" : "adrese"}`}
                      sx={{ backgroundColor: "#f2f4f7", color: "#475467" }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddLocationAltOutlinedIcon />}
                      onClick={startAddingAddress}
                      disabled={addingAddress || savingAddress}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 700,
                        boxShadow: "none",
                      }}
                    >
                      Adaugă adresă
                    </Button>
                  </Stack>
                }
              />

              <Collapse in={addingAddress} unmountOnExit>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    mb: 2,
                    borderRadius: 3,
                    borderColor: "#c7d2fe",
                    backgroundColor: "#f8faff",
                  }}
                >
                  <Typography sx={{ fontWeight: 750, color: "#1e1b4b", mb: 2 }}>
                    Adresă nouă
                  </Typography>
                  <AddressForm
                    value={newAddress}
                    onChange={setNewAddress}
                    errors={addressErrors}
                    onSave={handleAddAddress}
                    onCancel={() => {
                      setAddingAddress(false);
                      setAddressErrors({});
                    }}
                    saving={savingAddress}
                    submitLabel="Salvează adresa"
                  />
                </Paper>
              </Collapse>

              {addresses.length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    textAlign: "center",
                    borderRadius: 3,
                    borderStyle: "dashed",
                    borderColor: "#cbd5e1",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <LocationOnOutlinedIcon
                    sx={{ color: "#98a2b3", fontSize: 34 }}
                  />
                  <Typography
                    sx={{ fontWeight: 700, color: "#344054", mt: 0.75 }}
                  >
                    Nu există nicio adresă salvată
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#667085", mt: 0.5 }}
                  >
                    Adaugă prima adresă pentru a o putea folosi în cerere.
                  </Typography>
                </Paper>
              ) : (
                <RadioGroup
                  value={selectedAddress}
                  onChange={(event) => handleAddressSelect(event.target.value)}
                  aria-label="Adresele furnizorului"
                  sx={{ gap: 1.25 }}
                >
                  {addresses.map((address) => {
                    const isEditing =
                      String(editingAddress?.address_id) ===
                      String(address.address_id);
                    const isSelected =
                      String(selectedAddress) === String(address.address_id);
                    const addressDetails = [
                      address.city,
                      address.state,
                      address.postal_code,
                      address.country || "Romania",
                    ]
                      .filter(Boolean)
                      .join(" · ");

                    return (
                      <Paper
                        key={address.address_id}
                        variant="outlined"
                        sx={{
                          p: isEditing ? { xs: 2, sm: 2.5 } : 1.5,
                          borderRadius: 3,
                          borderColor: isSelected ? "#818cf8" : "#e4e7ec",
                          backgroundColor: isSelected ? "#f8faff" : "#fff",
                          boxShadow: isSelected
                            ? "0 6px 18px rgba(79, 70, 229, 0.09)"
                            : "none",
                          transition:
                            "border-color 160ms ease, box-shadow 160ms ease",
                        }}
                      >
                        {isEditing ? (
                          <AddressForm
                            value={editingAddress}
                            onChange={setEditingAddress}
                            errors={addressErrors}
                            onSave={handleSaveEdit}
                            onCancel={() => {
                              setEditingAddress(null);
                              setAddressErrors({});
                            }}
                            saving={savingAddress}
                            submitLabel="Actualizează adresa"
                          />
                        ) : (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                          >
                            <FormControlLabel
                              value={String(address.address_id)}
                              control={<Radio size="small" />}
                              label=""
                              sx={{ m: 0, mt: 0.15 }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0, pt: 0.3 }}>
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                alignItems={{ xs: "flex-start", sm: "center" }}
                                spacing={1}
                              >
                                <Typography
                                  sx={{ fontWeight: 750, color: "#1d2939" }}
                                >
                                  {address.address_line1 ||
                                    "Adresă fără denumire"}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={
                                    CATEGORY_LABELS[address.categorie] ||
                                    "Fără categorie"
                                  }
                                  color={
                                    address.categorie ? "primary" : "default"
                                  }
                                  variant="outlined"
                                  sx={{ height: 24, fontSize: 12 }}
                                />
                              </Stack>
                              {address.address_line2 && (
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#475467", mt: 0.45 }}
                                >
                                  {address.address_line2}
                                </Typography>
                              )}
                              <Typography
                                variant="body2"
                                sx={{ color: "#667085", mt: 0.35 }}
                              >
                                {addressDetails ||
                                  "Detalii de localizare necompletate"}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={0.25}>
                              <Tooltip title="Editează adresa">
                                <IconButton
                                  size="small"
                                  aria-label="Editează adresa"
                                  onClick={() => startEditingAddress(address)}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Șterge adresa">
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label="Șterge adresa"
                                  onClick={() => setAddressToDelete(address)}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Stack>
                        )}
                      </Paper>
                    );
                  })}
                </RadioGroup>
              )}
            </Box>

            <Divider />

            <Box>
              <SectionTitle
                icon={<SecurityOutlinedIcon />}
                title="Date confidențiale"
                description="CNP-ul și datele actului de identitate sunt protejate prin parolă."
                action={
                  !confidentialPanelOpen ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LockOutlinedIcon />}
                      onClick={openConfidentialPanel}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 700,
                      }}
                    >
                      Deschide datele confidențiale
                    </Button>
                  ) : (
                    <Tooltip title="Închide secțiunea">
                      <IconButton
                        size="small"
                        onClick={closeConfidentialPanel}
                        aria-label="Închide datele confidențiale"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )
                }
              />

              <Collapse in={confidentialPanelOpen} unmountOnExit>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 3,
                    borderColor: "#d8b4fe",
                    background:
                      "linear-gradient(145deg, #fdfaff 0%, #ffffff 65%)",
                  }}
                >
                  {!passwordVerified ? (
                    <Box
                      component="form"
                      onSubmit={handlePasswordSubmit}
                      sx={{ maxWidth: 520, mx: "auto", py: { xs: 1, sm: 2 } }}
                    >
                      <Stack alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            display: "grid",
                            placeItems: "center",
                            borderRadius: "50%",
                            color: "#7e22ce",
                            backgroundColor: "#f3e8ff",
                          }}
                        >
                          <LockOutlinedIcon />
                        </Box>
                        <Typography sx={{ fontWeight: 750, color: "#1d2939" }}>
                          Acces protejat
                        </Typography>
                        <Typography
                          variant="body2"
                          align="center"
                          sx={{ color: "#667085" }}
                        >
                          Introdu parola pentru a vedea sau modifica datele
                          confidențiale.
                        </Typography>
                      </Stack>
                      <TextField
                        label="Parolă"
                        type="password"
                        value={passwordInput}
                        onChange={(event) => {
                          setPasswordInput(event.target.value);
                          if (passwordError) setPasswordError("");
                        }}
                        error={Boolean(passwordError)}
                        helperText={passwordError}
                        autoComplete="current-password"
                        autoFocus
                        fullWidth
                        sx={inputSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlinedIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={verifyingPassword}
                        startIcon={
                          verifyingPassword ? (
                            <CircularProgress size={17} color="inherit" />
                          ) : (
                            <SecurityOutlinedIcon />
                          )
                        }
                        sx={{
                          mt: 1.5,
                          py: 1.1,
                          borderRadius: 2.5,
                          textTransform: "none",
                          fontWeight: 750,
                          boxShadow: "none",
                          background:
                            "linear-gradient(135deg, #7e22ce, #4f46e5)",
                        }}
                      >
                        {verifyingPassword
                          ? "Se verifică..."
                          : "Verifică parola"}
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <Alert
                        severity={confidentialAlert.severity}
                        icon={
                          confidentialAlert.severity === "success" ? (
                            <CheckCircleOutlineIcon />
                          ) : undefined
                        }
                        sx={{ mb: 2.5, borderRadius: 2.5 }}
                      >
                        {confidentialAlert.text}
                      </Alert>

                      {confidentialStatus.hasConfidentialInfo && (
                        <Paper
                          component="section"
                          variant="outlined"
                          aria-labelledby="saved-confidential-info-title"
                          sx={{
                            mb: 2.5,
                            p: { xs: 2, sm: 2.25 },
                            borderRadius: 3,
                            borderColor: "#bbf7d0",
                            background:
                              "linear-gradient(145deg, #f0fdf4 0%, #ffffff 72%)",
                          }}
                        >
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ mb: 1.75 }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  display: "grid",
                                  placeItems: "center",
                                  borderRadius: 2,
                                  color: "#047857",
                                  backgroundColor: "#dcfce7",
                                }}
                              >
                                <StorageOutlinedIcon fontSize="small" />
                              </Box>
                              <Box>
                                <Typography
                                  id="saved-confidential-info-title"
                                  sx={{ fontWeight: 800, color: "#14532d" }}
                                >
                                  Date încărcate din baza de date
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#4b5563" }}
                                >
                                  Ultimele valori confirmate de server
                                </Typography>
                              </Box>
                            </Stack>
                            <Chip
                              size="small"
                              icon={<CheckCircleOutlineIcon />}
                              label="Salvate"
                              sx={{
                                color: "#047857",
                                backgroundColor: "#dcfce7",
                                fontWeight: 750,
                                "& .MuiChip-icon": { color: "#059669" },
                              }}
                            />
                          </Stack>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(3, minmax(0, 1fr))",
                              },
                              gap: 1.25,
                            }}
                          >
                            {CONFIDENTIAL_FIELDS.map(({ key, label }) => (
                              <Box
                                key={key}
                                sx={{
                                  minWidth: 0,
                                  p: 1.5,
                                  border: "1px solid #d1fae5",
                                  borderRadius: 2.25,
                                  backgroundColor: "rgba(255,255,255,0.9)",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#667085", fontWeight: 700 }}
                                >
                                  {label}
                                </Typography>
                                <Typography
                                  data-testid={`saved-confidential-${key}`}
                                  sx={{
                                    mt: 0.35,
                                    color: savedConfidentialInfo[key]
                                      ? "#1d2939"
                                      : "#98a2b3",
                                    fontWeight: savedConfidentialInfo[key]
                                      ? 750
                                      : 600,
                                    fontFamily: savedConfidentialInfo[key]
                                      ? "ui-monospace, SFMono-Regular, Menlo, monospace"
                                      : "inherit",
                                    overflowWrap: "anywhere",
                                  }}
                                >
                                  {savedConfidentialInfo[key] || "Necompletat"}
                                </Typography>
                              </Box>
                            ))}
                          </Box>

                          <Typography
                            variant="caption"
                            sx={{ display: "block", color: "#667085", mt: 1.5 }}
                          >
                            Modificările făcute mai jos apar în acest rezumat
                            numai după salvarea lor cu succes.
                          </Typography>
                        </Paper>
                      )}

                      <Box
                        component="form"
                        onSubmit={(event) => {
                          event.preventDefault();
                          handleSaveConfidentialInfo();
                        }}
                        noValidate
                      >
                        {confidentialErrors.form && (
                          <Alert
                            severity="error"
                            sx={{ mb: 2, borderRadius: 2 }}
                          >
                            {confidentialErrors.form}
                          </Alert>
                        )}
                        <Typography
                          variant="body2"
                          sx={{ color: "#667085", mb: 1.5 }}
                        >
                          Completează cel puțin unul dintre câmpurile de mai
                          jos.
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              md: "repeat(2, minmax(0, 1fr))",
                            },
                            gap: 2,
                          }}
                        >
                          <TextField
                            label="CNP (opțional)"
                            value={confidentialInfo.cnp}
                            onChange={(event) => {
                              setConfidentialInfo((current) => ({
                                ...current,
                                cnp: event.target.value.replace(/\D/g, ""),
                              }));
                              setConfidentialErrors({});
                            }}
                            fullWidth
                            inputProps={{ inputMode: "numeric", maxLength: 13 }}
                            sx={{ ...inputSx, gridColumn: { md: "1 / -1" } }}
                          />
                          <TextField
                            label="Serie CI (opțional)"
                            value={confidentialInfo.serieCI}
                            onChange={(event) => {
                              setConfidentialInfo((current) => ({
                                ...current,
                                serieCI: event.target.value.toUpperCase(),
                              }));
                              setConfidentialErrors({});
                            }}
                            fullWidth
                            inputProps={{ maxLength: 4 }}
                            sx={inputSx}
                          />
                          <TextField
                            label="Număr CI (opțional)"
                            value={confidentialInfo.nrCI}
                            onChange={(event) => {
                              setConfidentialInfo((current) => ({
                                ...current,
                                nrCI: event.target.value.replace(/\D/g, ""),
                              }));
                              setConfidentialErrors({});
                            }}
                            fullWidth
                            inputProps={{ inputMode: "numeric", maxLength: 10 }}
                            sx={inputSx}
                          />
                        </Box>

                        <Stack
                          direction={{ xs: "column-reverse", sm: "row" }}
                          justifyContent="flex-end"
                          spacing={1.25}
                          sx={{ mt: 2.5 }}
                        >
                          <Button
                            type="button"
                            variant="text"
                            onClick={closeConfidentialPanel}
                            disabled={savingConfidentialInfo}
                            sx={{ color: "#475467" }}
                          >
                            Închide
                          </Button>
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={savingConfidentialInfo}
                            startIcon={
                              savingConfidentialInfo ? (
                                <CircularProgress size={17} color="inherit" />
                              ) : (
                                <SaveOutlinedIcon />
                              )
                            }
                            sx={{
                              borderRadius: 2,
                              px: 2.5,
                              textTransform: "none",
                              fontWeight: 750,
                              boxShadow: "none",
                            }}
                          >
                            {savingConfidentialInfo
                              ? "Se salvează..."
                              : confidentialStatus.hasConfidentialInfo
                                ? "Actualizează datele"
                                : "Salvează datele"}
                          </Button>
                        </Stack>
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Collapse>
            </Box>
          </Stack>
        )}
      </CardContent>

      <Dialog
        open={Boolean(addressToDelete)}
        onClose={() => !deletingAddress && setAddressToDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 750 }}>
          Ștergi această adresă?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#667085" }}>
            Adresa „{addressToDelete?.address_line1 || "fără denumire"}” va fi
            eliminată definitiv din lista furnizorului.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setAddressToDelete(null)}
            disabled={deletingAddress}
            sx={{ textTransform: "none" }}
          >
            Renunță
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteAddress}
            disabled={deletingAddress}
            startIcon={
              deletingAddress ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlineIcon />
              )
            }
            sx={{ borderRadius: 2, textTransform: "none", boxShadow: "none" }}
          >
            {deletingAddress ? "Se șterge..." : "Șterge adresa"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4500}
        onClose={() =>
          setNotification((current) => ({ ...current, open: false }))
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={notification.severity}
          variant="filled"
          onClose={() =>
            setNotification((current) => ({ ...current, open: false }))
          }
          sx={{ borderRadius: 2, boxShadow: 4 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Card>
  );
}
